import React from 'react';
import { Branch, DayKey, dayKeys, dayShort, DayHours } from './BranchesContext';
export type BranchStatus = {
  open: boolean;
  /** Short human label, e.g. "Open now", "Closed · opens 8:00 AM", "Temporarily closed". */
  label: string;
};
function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(n => parseInt(n, 10));
  return (h || 0) * 60 + (m || 0);
}
function formatTime(hhmm: string): string {
  const [hRaw, m] = hhmm.split(':').map(n => parseInt(n, 10));
  const h = hRaw % 24;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${period}`;
}
function jsDayToKey(jsDay: number): DayKey {
  // JS: 0 = Sunday ... 6 = Saturday. dayKeys starts at Monday.
  return dayKeys[(jsDay + 6) % 7];
}
function localDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
// Resolve the effective hours for a given date, honoring special-day overrides.
function hoursForDate(branch: Branch, date: Date): DayHours {
  const special = branch.specialDays?.find(s => s.date === localDateKey(date));
  if (special) {
    if (special.closed) return {
      closed: true,
      open: '',
      close: ''
    };
    return {
      closed: false,
      open: special.open || branch.hours[jsDayToKey(date.getDay())].open,
      close: special.close || branch.hours[jsDayToKey(date.getDay())].close
    };
  }
  return branch.hours[jsDayToKey(date.getDay())];
}
function findNextOpen(branch: Branch, from: Date): string | null {
  // Scan up to 7 days ahead for the next opening time.
  for (let i = 0; i < 8; i++) {
    const d = new Date(from);
    d.setDate(from.getDate() + i);
    const h = hoursForDate(branch, d);
    if (h.closed || !h.open) continue;
    const openMin = toMinutes(h.open);
    if (i === 0) {
      // Today: only counts if it opens later than now.
      const nowMin = from.getHours() * 60 + from.getMinutes();
      if (openMin <= nowMin) continue;
      return `opens ${formatTime(h.open)}`;
    }
    const dayLabel = i === 1 ? 'tomorrow' : dayShort[jsDayToKey(d.getDay())];
    return `opens ${dayLabel} ${formatTime(h.open)}`;
  }
  return null;
}
export function getBranchStatus(branch: Branch, now: Date = new Date()): BranchStatus {
  if (branch.temporaryClosure?.active) {
    return {
      open: false,
      label: 'Temporarily closed'
    };
  }
  const today = hoursForDate(branch, now);
  const nowMin = now.getHours() * 60 + now.getMinutes();
  if (!today.closed && today.open && today.close) {
    const openMin = toMinutes(today.open);
    let closeMin = toMinutes(today.close);
    // Overnight (e.g. 18:00–02:00): treat close as next day.
    const overnight = closeMin <= openMin;
    if (overnight) closeMin += 24 * 60;
    const adjustedNow = overnight && nowMin < openMin ? nowMin + 24 * 60 : nowMin;
    if (adjustedNow >= openMin && adjustedNow < closeMin) {
      return {
        open: true,
        label: `Open now · until ${formatTime(today.close)}`
      };
    }
  }
  const next = findNextOpen(branch, now);
  return {
    open: false,
    label: next ? `Closed · ${next}` : 'Closed'
  };
}
export function BranchStatusBadge({
  branch,
  className = ''
}: {
  branch: Branch;
  className?: string;
}) {
  const status = getBranchStatus(branch);
  return <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${status.open ? 'text-green-700' : 'text-gray-500'} ${className}`} data-id="element-948">
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${status.open ? 'bg-green-500' : 'bg-gray-400'}`} data-id="element-949" />
      {status.label}
    </span>;
}